/**
 * ACC Football Championship Game Tiebreaker — 2026 policy
 * (announced July 15, 2026; replaces the pre-2026 procedure entirely)
 *
 * Source: ACC Football Tiebreaker Policy, July 2026 (theacc.com)
 *
 * Guiding principles per the ACC:
 *   1. Head-to-head results matter most.
 *   2. No team is rewarded/penalized for playing an alternate number of
 *      conference games (relevant in 2026: 12 teams play 9 conf. games,
 *      5 play 8).
 *   3. When head-to-head can't separate teams, the team with the strongest
 *      overall body of work — measured by SportSource Analytics' Team
 *      Success Ranking (SSR) — advances.
 *
 * Two-Team Tie:
 *   1. Head-to-head (conference game only)
 *   2. Better SSR
 *   3. Draw (Commissioner or designee)
 *
 * Three(+)-Team Tie:
 *   A. If all tied teams are common opponents (everyone played everyone):
 *      i.   Best record among the tied teams (head-to-head win pct)
 *      ii.  Best SSR
 *      iii. Draw
 *      -> if still tied after any step, RESTART the entire tiebreaker,
 *         including re-defining who counts as "tied" (see below).
 *   B. If not all tied teams are common opponents:
 *      i.   Team that defeated every other tied team advances & is removed
 *      ii.  Team that lost to every other tied team is eliminated & removed
 *      iii. Best SSR advances & is removed
 *      iv.  Draw
 *      -> restart as needed until the group is resolved.
 *
 * Step 1 ("Define the Tied Teams") matters because of the mixed 8-/9-game
 * 2026 schedule: a team isn't just "tied" by identical record — a team
 * that played an alternate number of conference games is pulled into the
 * tied group if it matches the leader's win *or* loss count. This module
 * re-applies that definition every time it restarts after peeling a team
 * off, per the policy's explicit "including re-defining tied teams"
 * instruction.
 *
 * NOTE on SSR direction: the ACC calls this a "ranking," which normally
 * means lower-is-better (rank #1 = best). This module defaults to that
 * convention. If your ssarankings values are a rating *score* instead
 * (higher = better), pass options.ssrHigherIsBetter = true.
 *
 * Inputs:
 * - tiedTeams: array of { team: string } — the teams already known to be tied
 * - confTeams: standings array [{ team, conferenceWins, conferenceGames, conferenceLosses? }]
 * - games: array of games [{ homeTeam, awayTeam, winner, conferenceGame, exempt }]
 * - options: {
 *     ssarankings: { [team]: number },   // SSR value per team
 *     ssrHigherIsBetter: boolean,        // default false (lower rank = better)
 *     rng: () => number                  // default Math.random
 *   }
 *
 * Returns: ordered array of { team } (best/highest seed first).
 */

const EPS = 1e-9;
// Exact-equality shortcut first: handles Infinity/-Infinity (Infinity - Infinity
// is NaN, which would otherwise make nearlyEqual(Infinity, Infinity) false)
// and NaN (NaN === NaN is false, so two NaN scores correctly compare unequal).
const nearlyEqual = (a, b) => a === b || Math.abs(a - b) < EPS;

export const resolveACCTie = (tiedTeams, confTeams, games, options = {}) => {

    if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];

  return resolveACCGroup(tiedTeams, confTeams, games, options);
};

// ---------- Dispatcher ----------
const resolveACCGroup = (group, confTeams, games, options = {}) => {

  if (group.length === 0) return [];
  if (group.length === 1) return group;
  if (group.length === 2) {
    return resolveACCTwoTeamTie(group[0], group[1], games, options);
    }
   
  return resolveACCMultiTeamTie(group, confTeams, games, options);
};

// ---------- Two-team tie ----------
const resolveACCTwoTeamTie = (A, B, games, options = {}) => {

  const { rng = Math.random } = options;

  // Step 1: head-to-head
  const h2h = headToHeadResult(A.team, B.team, games);
  if (h2h) return h2h === A.team ? [A, B] : [B, A];

  // Step 2: SSR
  const ssr = compareSSR(A.team, B.team, options);
    if (ssr) return ssr === A.team ? [A, B] : [B, A];

  // Step 3: draw
  return rng() < 0.5 ? [A, B] : [B, A];
};

// ---------- Three(+)-team tie ----------
const resolveACCMultiTeamTie = (group, confTeams, games, options = {}) => {
  // "Restart... including re-defining tied teams": before applying any
  // criterion, re-check whether every team in the current group still
  // qualifies as tied under the alternate-schedule-aware definition.
  const { tied, rest } = redefineTiedTeams(group, confTeams);
  if (tied.length < group.length) {
    return [
      ...resolveACCGroup(tied, confTeams, games, options),
      ...resolveACCGroup(rest, confTeams, games, options)
    ];
  }

  if (allCommonOpponents(group, games)) {
    // ---- Branch A: everyone played everyone ----
    const scored = group.map(t => ({ team: t, pct: groupH2HPct(t.team, group, games) }));
    const split = splitByScore(scored, group);
    if (split) {
      return [
        ...resolveACCGroup(split.top, confTeams, games, options),
        ...resolveACCGroup(split.rest, confTeams, games, options)
      ];
    }
    return resolveViaSSRThenDraw(group, confTeams, games, options);
  }

  // ---- Branch B: not all common opponents ----
  const beatAll = group.filter(t => beatEveryoneElse(t, group, games));
  if (beatAll.length === 1) {
    const top = beatAll[0];
    const rest2 = group.filter(t => t.team !== top.team);
    return [top, ...resolveACCGroup(rest2, confTeams, games, options)];
  }

  const lostToAll = group.filter(t => lostToEveryoneElse(t, group, games));
  if (lostToAll.length > 0) {
    const rest2 = group.filter(t => !lostToAll.some(l => l.team === t.team));
    return [
      ...resolveACCGroup(rest2, confTeams, games, options),
      ...resolveACCGroup(lostToAll, confTeams, games, options)
    ];
  }

  return resolveViaSSRThenDraw(group, confTeams, games, options);
};

// SSR then draw, applied to a group that head-to-head couldn't separate.
// Peels off (or draws) one team at a time and restarts, per policy.
const resolveViaSSRThenDraw = (group, confTeams, games, options = {}) => {
  const { ssarankings = null, rng = Math.random } = options;

  if (ssarankings) {
    const scored = group.map(t => ({ team: t, score: ssrScoreForSort(t.team, options) }));
    const split = splitByScore(scored, group);
    if (split && split.top.length === 1) {
      const top = split.top[0];
      const rest = group.filter(t => t.team !== top.team);
      return [top, ...resolveACCGroup(rest, confTeams, games, options)];
    }
    if (split) {
      // multiple teams still tied for best SSR, rest are clearly worse
      return [
        ...resolveACCGroup(split.top, confTeams, games, options),
        ...resolveACCGroup(split.rest, confTeams, games, options)
      ];
    }
    // SSR ties across the whole group -> fall through to draw
  }

  // Draw: pick one team at random to advance, restart with the rest.
  const idx = Math.floor(rng() * group.length);
  const winner = group[idx];
  const rest = group.filter((_, i) => i !== idx);
  return [winner, ...resolveACCGroup(rest, confTeams, games, options)];
};

// ---------- "Define/redefine the tied teams" (Step 1) ----------
// A team belongs in the tied group if it has the best conference win pct,
// OR — because of the alternate-schedule-length season — it matches the
// leader's win count or loss count even at a different pct.
const redefineTiedTeams = (group, confTeams) => {
  const withRecord = group.map(t => {
    const rec = confTeams.find(c => c.team === t.team) || {};
    const games_ = rec.conferenceGames || 0;
    const wins = rec.conferenceWins || 0;
    const losses = typeof rec.conferenceLosses === 'number' ? rec.conferenceLosses : games_ - wins;
      const pct = games_ > 0 ? wins / games_ : 0;
      const avg = t.avg;
    return { team: t, wins, losses, pct };
  });

  const maxPct = Math.max(...withRecord.map(x => x.pct));
  const leaders = withRecord.filter(x => nearlyEqual(x.pct, maxPct));
  const leaderWins = leaders[0].wins;
  const leaderLosses = leaders[0].losses;

  const tied = [];
  const rest = [];
  for (const x of withRecord) {
    if (nearlyEqual(x.pct, maxPct) || x.wins === leaderWins || x.losses === leaderLosses) {
      tied.push(x.team);
    } else {
      rest.push(x.team);
    }
  }
  return { tied, rest };
};

// ---------- Helpers ----------
const isCountable = g => !g.exempt && g.conferenceGame;

const gamesBetween = (teamA, teamB, games) =>
  games.filter(
    g =>
      isCountable(g) &&
      ((g.homeTeam === teamA && g.awayTeam === teamB) || (g.homeTeam === teamB && g.awayTeam === teamA))
  );

// Returns the winning team's name if one team won more meetings, else null.
const headToHeadResult = (teamA, teamB, games) => {
  const matches = gamesBetween(teamA, teamB, games);
  if (!matches.length) return null;
  const aWins = matches.filter(m => m.winner === teamA).length;
  const bWins = matches.filter(m => m.winner === teamB).length;
  if (aWins > bWins) return teamA;
  if (bWins > aWins) return teamB;
  return null;
};

const allCommonOpponents = (group, games) => {
  for (let i = 0; i < group.length; i++) {
    for (let j = i + 1; j < group.length; j++) {
      if (gamesBetween(group[i].team, group[j].team, games).length === 0) return false;
    }
  }
  return true;
};

const groupH2HPct = (team, group, games) => {
  let wins = 0;
  let total = 0;
  for (const other of group) {
    if (other.team === team) continue;
    const matches = gamesBetween(team, other.team, games);
    total += matches.length;
    wins += matches.filter(m => m.winner === team).length;
  }
  return total > 0 ? wins / total : 0;
};

const beatEveryoneElse = (team, group, games) =>
  group
    .filter(o => o.team !== team.team)
    .every(other => headToHeadResult(team.team, other.team, games) === team.team);

const lostToEveryoneElse = (team, group, games) =>
  group
    .filter(o => o.team !== team.team)
    .every(other => headToHeadResult(team.team, other.team, games) === other.team);

// score: lower is better by default (a "ranking"); flip with options.ssrHigherIsBetter
const ssrScoreForSort = (team, options) => {
  const { ssarankings, ssrHigherIsBetter = false } = options;
  const raw = ssarankings?.[team];
  // Internally we always normalize to "higher score = better". A team with
  // no SSR data must always lose the comparison, so it always gets -Infinity
  // here -- never Infinity, regardless of which direction is "better".
  if (raw == null) return -Infinity;
  return ssrHigherIsBetter ? raw : -raw;
};

const compareSSR = (teamA, teamB, options) => {
  const { ssarankings } = options;
  if (!ssarankings) return null;
  const aRaw = ssarankings[teamA];
  const bRaw = ssarankings[teamB];
  if (aRaw == null || bRaw == null) return null;
  const higherIsBetter = options.ssrHigherIsBetter ?? false;
  if (aRaw === bRaw) return null;
  const aBetter = higherIsBetter ? aRaw > bRaw : aRaw < bRaw;
  return aBetter ? teamA : teamB;
};

const splitByScore = (scored, group) => {
  const maxScore = Math.max(...scored.map(s => s.score ?? s.pct));
  const top = scored.filter(s => nearlyEqual(s.score ?? s.pct, maxScore)).map(s => s.team);
  const rest = scored.filter(s => !nearlyEqual(s.score ?? s.pct, maxScore)).map(s => s.team);
  // Defensive: top.length should never be 0 (something must equal the max
  // of its own array) — but if a NaN/Infinity edge case ever produces one
  // anyway, treat it as "no separation" rather than recursing on an
  // unchanged group forever.
  if (top.length === 0 || top.length === group.length) return null;
  return { top, rest };
};